import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getAuthToken, getAuthType, getUserData } from '../../utils/localStorage';

export interface UserData {
  user_id: number;
  name: string;
  bio: string;
  date_joined: string;
  email: string;
  headline: string;
  image: string | null;
  is_verified: boolean;
  is_expert: boolean;
  languages: string[];
  phone_number: string | null;
  sebi_qualification: string;
  sebi_reg_number: string;
  user_exists: boolean;
  years_of_xp: number;
}

interface UserState {
  user: UserData | null;
  authToken: string | null;
  authType: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

// Mock user for dev mode (when REACT_APP_SKIP_LOGIN=true)
const mockUser: UserData = {
  user_id: 0,
  name: 'Dev User',
  bio: 'Development test user',
  date_joined: new Date().toISOString(),
  email: 'dev@test.com',
  headline: 'Developer',
  image: null,
  is_verified: true,
  is_expert: false,
  languages: ['English'],
  phone_number: null,
  sebi_qualification: '',
  sebi_reg_number: '',
  user_exists: true,
  years_of_xp: 0,
};

const skipLogin = process.env.REACT_APP_SKIP_LOGIN === 'true';

// Restore auth state from localStorage (for page refreshes)
const storedToken = getAuthToken();
const storedAuthType = getAuthType();
const storedUser = getUserData();

const initialState: UserState = {
  user: skipLogin ? mockUser : storedUser,
  authToken: skipLogin ? 'dev-token' : storedToken,
  authType: skipLogin ? 'dev' : storedAuthType,
  isAuthenticated: skipLogin || !!storedToken,
  isLoading: false,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<UserData | null>) => {
      state.user = action.payload;
    },
    updateUser: (state, action: PayloadAction<Partial<UserData>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
    clearUser: (state) => {
      state.user = null;
    },
    setAuthToken: (state, action: PayloadAction<string>) => {
      state.authToken = action.payload;
      state.isAuthenticated = true;
    },
    setAuthType: (state, action: PayloadAction<string>) => {
      state.authType = action.payload;
    },
    setAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    logout: (state) => {
      state.user = null;
      state.authToken = null;
      state.authType = null;
      state.isAuthenticated = false;
    },
  },
});

export const {
  setUser,
  updateUser,
  clearUser,
  setAuthToken,
  setAuthType,
  setAuthenticated,
  setLoading,
  logout
} = userSlice.actions;

export default userSlice.reducer;
